import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { GraduationCap, CheckCircle2, Play, Loader2, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { BP_ID, BP_DAYS, BP_WEEKS, BP_ORDER, bpDayKey } from "@/lib/beginnerProgram";
import { cn } from "@/lib/utils";

function BpDayCard({ day, week, doneOn, names, onStart, starting, isNext, L }) {
  const k = bpDayKey(week.n, day.key);
  return (
    <div className={cn("rounded-2xl border bg-card p-4", isNext && "border-primary/50 ring-1 ring-primary/20")} data-testid={`bp-day-${k}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-display font-bold">{day[L]}</div>
        {doneOn ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-testid={`bp-done-${k}`}><CheckCircle2 className="h-3.5 w-3.5" /> {L === "id" ? "Selesai" : "Done"}</span>
        ) : isNext && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">{L === "id" ? "Berikutnya" : "Next"}</span>}
      </div>
      <ul className="mt-3 space-y-1.5 text-sm">
        {day.exercises.map((n) => (
          <li key={n} className="flex items-center justify-between gap-2">
            <span className="truncate">{names(n)}</span>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{week.sets} × {week.reps}</span>
          </li>
        ))}
      </ul>
      <Button onClick={() => onStart(day, k)} disabled={!!starting} variant={doneOn ? "outline" : "default"} className="mt-4 w-full gap-2" data-testid={`bp-start-${k}`}>
        {starting === k ? <Loader2 className="h-4 w-4 animate-spin" /> : doneOn ? <RotateCcw className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        {doneOn ? (L === "id" ? "Ulangi" : "Repeat") : (L === "id" ? "Mulai" : "Start")}
      </Button>
    </div>
  );
}

export function BeginnerProgram({ onStarted }) {
  const { lang } = useI18n();
  const L = lang === "id" ? "id" : "en";
  const qc = useQueryClient();
  const [starting, setStarting] = useState(null);
  const { data: prog } = useQuery({ queryKey: ["beginner-program"], queryFn: async () => (await api.get("/beginner-program/progress")).data });
  const { data: core } = useQuery({ queryKey: ["exercises", "core"], queryFn: async () => (await api.get("/exercises", { params: { core: true } })).data });
  const done = prog?.completed || {};
  const doneCount = BP_ORDER.filter((k) => done[k]).length;
  const nextKey = BP_ORDER.find((k) => !done[k]);
  const [week, setWeek] = useState(null);
  const curWeek = week ?? (nextKey ? Number(nextKey.slice(1, 2)) : 4);
  const w = BP_WEEKS[curWeek - 1];
  const byName = useMemo(() => Object.fromEntries((core || []).map((e) => [e.name.toLowerCase(), e])), [core]);
  const names = (n) => { const e = byName[n.toLowerCase()]; return e ? (L === "id" ? e.name_id || e.name : e.name) : n; };

  const start = async (day, key) => {
    setStarting(key);
    try {
      const logs = day.exercises.map((n) => byName[n.toLowerCase()]).filter(Boolean).map((e) => ({
        exercise_id: e.id, exercise_name: L === "id" ? e.name_id || e.name : e.name, type: e.type,
        sets: Array.from({ length: w.sets }, () => ({ weight: "", reps: e.type === "time_distance" ? "" : w.reps, warmup: false })),
      }));
      const name = `${L === "id" ? "Program Pemula" : "Beginner Program"} · ${L === "id" ? "Minggu" : "Week"} ${w.n} · ${day[L]}`;
      const { data: session } = await api.post("/sessions", { name, program: BP_ID, program_day: key });
      await api.put(`/sessions/${session.id}`, { logs });
      await qc.invalidateQueries({ queryKey: ["active-session"] });
      onStarted?.();
      toast.success(L === "id" ? "Sesi program dimulai" : "Program session started");
    } catch { toast.error("Error"); } finally { setStarting(null); }
  };

  return (
    <div className="space-y-4 rounded-3xl border bg-gradient-to-br from-emerald-500/10 to-transparent p-5" data-testid="beginner-program">
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"><GraduationCap className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg font-bold tracking-tight">{L === "id" ? "Program Pemula 4 Minggu" : "4-Week Beginner Program"}</h3>
          <p className="text-sm text-muted-foreground">{L === "id" ? "Full body 3x seminggu dengan 1 hari istirahat di antaranya (mis. Sen–Rab–Jum). Beban naik bertahap tiap minggu." : "Full body 3x a week with a rest day in between (e.g. Mon–Wed–Fri). The load increases every week."}</p>
        </div>
      </div>
      <div>
        <div className="mb-1.5 flex justify-between text-xs font-medium"><span className="text-muted-foreground">{L === "id" ? "Progres program" : "Program progress"}</span><span className="tabular-nums text-primary" data-testid="bp-progress">{doneCount}/{BP_ORDER.length} {L === "id" ? "sesi" : "sessions"}</span></div>
        <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${(doneCount / BP_ORDER.length) * 100}%` }} /></div>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="bp-weeks">
        {BP_WEEKS.map((wk) => {
          const all = BP_DAYS.every((d) => done[bpDayKey(wk.n, d.key)]);
          return (
            <button key={wk.n} onClick={() => setWeek(wk.n)} data-testid={`bp-week-${wk.n}`}
              className={cn("inline-flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors",
                curWeek === wk.n ? "border-primary bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground")}>
              {all && <CheckCircle2 className="h-3.5 w-3.5" />}{L === "id" ? "Minggu" : "Week"} {wk.n}
            </button>
          );
        })}
      </div>
      <div className="rounded-2xl border bg-card/60 p-3 text-sm" data-testid="bp-week-info">
        <span className="font-semibold">{w[L].focus} · {w.sets} set × {w.reps} rep.</span> <span className="text-muted-foreground">{w[L].tip}</span>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {BP_DAYS.map((d) => (
          <BpDayCard key={d.key} day={d} week={w} doneOn={done[bpDayKey(w.n, d.key)]} names={names} onStart={start}
            starting={starting} isNext={nextKey === bpDayKey(w.n, d.key)} L={L} />
        ))}
      </div>
    </div>
  );
}
